import React from 'react';
import { AppButton } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';

export type ConsentFormBottomBarMode = 'create' | 'edit';

export interface ConsentFormBottomBarProps {
  mode: ConsentFormBottomBarMode;
  isSubmitting: boolean;
  isDeleting?: boolean;
  formId: string;
  onBack: () => void;
  onDelete?: () => void;
  canPrint?: boolean;
  onClickThankYouPrint?: () => void;
  onClickRequestPrint?: () => void;
  onClickConsentFormPrint?: () => void;
}

/**
 * Shared footer for consent create/edit — print actions stay disabled until PDF wave.
 */
const ConsentFormBottomBar: React.FC<ConsentFormBottomBarProps> = ({
  mode,
  isSubmitting,
  isDeleting = false,
  formId,
  onBack,
  onDelete,
  canPrint = false,
  onClickThankYouPrint,
  onClickRequestPrint,
  onClickConsentFormPrint,
}) => {
  const sidebarOpen = useSidebarOpen();
  const { colors } = useTheme();
  const showDelete = mode === 'edit' && typeof onDelete === 'function';
  const isBusy = isSubmitting || isDeleting;

  const left = (
    <div className="flex gap-2">
      <AppButton
        disabled={!canPrint || isBusy}
        onClick={onClickThankYouPrint}
        type="primary"
        className="px-4 py-2 font-medium text-white bg-green-600 rounded hover:!bg-green-700 disabled:bg-green-600 disabled:text-white disabled:opacity-50"
      >
        御礼状印刷
      </AppButton>
      <AppButton
        disabled={!canPrint || isBusy}
        onClick={onClickRequestPrint}
        type="primary"
        className="px-4 py-2 font-medium text-white bg-green-600 rounded hover:!bg-green-700 disabled:bg-green-600 disabled:text-white disabled:opacity-50"
      >
        発行依頼印刷
      </AppButton>
      <AppButton
        disabled={!canPrint || isBusy}
        onClick={onClickConsentFormPrint}
        type="primary"
        className="px-4 py-2 font-medium text-white bg-green-600 rounded hover:!bg-green-700 disabled:bg-green-600 disabled:text-white disabled:opacity-50"
      >
        同意書印刷
      </AppButton>
    </div>
  );

  const right = (
    <div className="flex gap-4">
      {showDelete && (
        <AppButton
          type="primary"
          disabled={isBusy}
          loading={isDeleting}
          onClick={onDelete}
          icon={<i className="fa fa-trash-o" aria-hidden="true" />}
          className="px-4 py-2 font-medium text-white bg-blue-400 rounded hover:!bg-blue-500 disabled:bg-blue-300 disabled:text-white disabled:cursor-not-allowed"
        >
          削除
        </AppButton>
      )}
      <AppButton
        type="default"
        onClick={onBack}
        disabled={isBusy}
        className="px-4 py-2 font-medium rounded"
      >
        一覧に戻る
      </AppButton>
      <AppButton
        type="primary"
        htmlType="submit"
        form={formId}
        loading={isSubmitting}
        disabled={!isSubmitting && isBusy}
        className="px-12 py-2 font-medium text-white rounded"
        style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
      >
        <i className="mr-2 fa fa-paper-plane" aria-hidden="true" /> 更新
      </AppButton>
    </div>
  );

  return (
    <BottomBar
      variant="action"
      visible={true}
      left={left}
      right={right}
      fixed
      className={sidebarOpen ? 'left-60' : 'left-0'}
    />
  );
};

export default ConsentFormBottomBar;
